import { Button, Typography } from "antd";
import { useEffect, useRef, useState } from "react";

type InlineOtpVerifyPanelProps = {
  title: string;
  description: string;
  isVerifying: boolean;
  verifyButtonLabel?: string;
  onVerify: (otp: string) => void;
  onCancel: () => void;
  onResend: () => void;
};

const OTP_LENGTH = 6;

export default function InlineOtpVerifyPanel(props: InlineOtpVerifyPanelProps) {
  const {
    title,
    description,
    isVerifying,
    verifyButtonLabel = "Verify",
    onVerify,
    onCancel,
    onResend,
  } = props;
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 0);
  }, []);

  const otp = digits.join("");

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter" && otp.length === OTP_LENGTH && !isVerifying) {
      onVerify(otp);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "10%",
        zIndex: 10,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
          padding: 24,
          width: 420,
          maxWidth: "90%",
        }}
      >
        <Typography.Title level={5}>{title}</Typography.Title>
        <Typography.Paragraph>{description}</Typography.Paragraph>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isVerifying}
              style={{
                width: 44,
                height: 48,
                textAlign: "center",
                fontSize: 20,
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                outline: "none",
              }}
            />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
          <div>
            <Button type="link" onClick={onResend} disabled={isVerifying}>
              Resend OTP
            </Button>
            <Button type="link" onClick={onCancel} disabled={isVerifying}>
              Cancel
            </Button>
          </div>
          <Button
            type="primary"
            disabled={otp.length !== OTP_LENGTH || isVerifying}
            loading={isVerifying}
            onClick={() => onVerify(otp)}
          >
            {verifyButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
